import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  ArrowRight, ArrowLeft, PenTool, Store, Megaphone, Users, Compass,
  CalendarDays, Sparkles, Mail, DollarSign, ChevronRight, Shield, 
  QrCode, Video, BarChart3, Globe, CreditCard, UserPlus
} from 'lucide-react';

const DEMO_STEPS = [
  {
    id: 'signature',
    title: 'Create Your Email Signature',
    desc: 'Choose from 11 professional templates — Executive, Modern, Bold, Elegant, and more. Add your photo, logo, QR code, social links, and custom messages. Preview it live as you build.',
    icon: PenTool,
    color: 'from-blue-600 to-cyan-500',
    features: [
      '11 professional templates with live preview',
      'Upload profile photo & company logo',
      'Add custom message, tagline & legal disclaimer',
      'Choose fonts, colors & layout to match your brand',
      'One-click copy — paste into Gmail, Outlook or Apple Mail',
    ],
    cta: 'Build Your Signature',
    ctaLink: '/signatures',
  },
  {
    id: 'marketplace',
    title: 'List on the Ad Marketplace',
    desc: 'Publish your signature to the marketplace so businesses can advertise on it. You approve or reject every request — you stay in control while earning passive income.',
    icon: Store,
    color: 'from-purple-600 to-violet-500',
    features: [
      'Set your flat-rate ad pricing ($20/mo per slot)',
      'Review and approve/reject ad requests',
      'Dynamic ads update automatically — no reinstall needed',
      'Earn 50% of every ad placement',
    ],
    cta: 'View Marketplace',
    ctaLink: '/signatures?tab=marketplace',
  },
  {
    id: 'advertise',
    title: 'Advertise on Others\' Emails',
    desc: 'Find professionals in your industry and place your ad on their email signature. Every email they send puts your business in front of their contacts.',
    icon: Megaphone,
    color: 'from-amber-500 to-orange-500',
    features: [
      'Browse professionals by industry & location',
      'AI-recommended partners for maximum reach',
      'Your ad appears in every email they send',
      'Track performance with built-in analytics',
    ],
    cta: 'Find Ad Slots',
    ctaLink: '/signatures?tab=marketplace',
  },
  {
    id: 'connections',
    title: 'Build Your Network',
    desc: 'Import contacts from Gmail or Outlook, discover local professionals, and let our AI match you with complementary businesses for cross-referrals.',
    icon: Users,
    color: 'from-emerald-500 to-green-600',
    features: [
      'Import contacts from Gmail & Microsoft Outlook',
      'AI-powered partner matching by profession & location',
      'Send connection requests & referrals',
      'Community directory with search & filters',
    ],
    cta: 'Explore Connections',
    ctaLink: '/explore',
  },
  {
    id: 'events',
    title: 'Virtual & Local Events',
    desc: 'Create networking events with auto-generated Jitsi Meet links, or host in-person meetups. Join industry groups and attend events to grow your network.',
    icon: CalendarDays,
    color: 'from-rose-500 to-pink-500',
    features: [
      'Create events with automatic video meeting links',
      'Use your own Zoom/Teams/Google Meet link',
      'RSVP tracking & attendee management',
      'Join industry & location-based groups',
    ],
    cta: 'Browse Events',
    ctaLink: '/events',
  },
  {
    id: 'referrals',
    title: 'Referral Program',
    desc: 'Invite professionals to join ConnectMail and earn rewards. Share your unique referral link — when they sign up and subscribe, you get 1 free month.',
    icon: UserPlus,
    color: 'from-teal-500 to-cyan-600',
    features: [
      'Unique referral link to share anywhere',
      'Earn 1 free month per successful referral',
      'Track referral stats on your dashboard',
      'Email invite form built right in',
    ],
    cta: 'Start Referring',
    ctaLink: '/dashboard',
  },
  {
    id: 'analytics',
    title: 'Analytics & QR Codes',
    desc: 'Track how your signature performs with built-in analytics. Generate a digital business card with QR code that anyone can scan to view your full profile.',
    icon: BarChart3,
    color: 'from-indigo-500 to-blue-600',
    features: [
      'Signature view & click analytics',
      'QR code business card — scan to connect',
      'Ad performance tracking for advertisers',
      'Leaderboard with badges & tier system',
    ],
    cta: 'View Analytics',
    ctaLink: '/signatures?tab=analytics',
  },
  {
    id: 'earn',
    title: 'Get Paid for Your Emails',
    desc: 'Turn every email you send into revenue. With ad placements on your signature, you earn money passively. Get paid via Stripe bank transfer or PayPal.',
    icon: DollarSign,
    color: 'from-yellow-500 to-amber-600',
    features: [
      'Earn 50% of ad placement revenue',
      'Choose payout: Bank (Stripe) or PayPal',
      'Track earnings in real-time on your dashboard',
      'Ads update dynamically — set it and forget it',
    ],
    cta: 'Start Earning',
    ctaLink: '/register',
  },
];

export default function DemoPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const step = DEMO_STEPS[currentStep];
  const Icon = step.icon;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="demo-page-title">How ConnectMail Works</h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">Your email signature becomes a billboard — and you earn from every email you send</p>
        </div>

        {/* Progress Bar */}
        <div className="flex items-center justify-center gap-1.5 mb-6" data-testid="demo-progress">
          {DEMO_STEPS.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setCurrentStep(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === currentStep ? 'bg-primary w-8' : i < currentStep ? 'bg-primary/40 w-4' : 'bg-muted-foreground/20 w-4'
              }`}
              data-testid={`demo-dot-${i}`}
            />
          ))}
        </div>

        {/* Step Counter */}
        <p className="text-center text-sm text-muted-foreground mb-4">
          Step {currentStep + 1} of {DEMO_STEPS.length}
        </p>

        {/* Main Card */}
        <Card className="overflow-hidden border-0 shadow-xl" data-testid={`demo-step-${step.id}`}>
          <div className={`bg-gradient-to-br ${step.color} p-6 sm:p-8 text-white`}>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Icon className="w-7 h-7" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl sm:text-2xl font-bold">{step.title}</h2>
                <p className="text-white/85 mt-2 text-sm leading-relaxed">{step.desc}</p>
              </div>
            </div>
          </div>
          <CardContent className="p-6 sm:p-8">
            <h3 className="font-semibold text-base mb-4">Key Features:</h3>
            <div className="space-y-2.5 mb-8">
              {step.features.map((f, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ChevronRight className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <span className="text-sm text-foreground/80">{f}</span>
                </div>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                disabled={currentStep === 0}
                className="gap-1.5"
                data-testid="demo-prev-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>

              <Link to={step.ctaLink}>
                <Button size="sm" className="gap-1.5" data-testid="demo-try-btn">
                  {step.cta} <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>

              {currentStep < DEMO_STEPS.length - 1 ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentStep(currentStep + 1)}
                  className="gap-1.5"
                  data-testid="demo-next-btn"
                >
                  Next <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Link to="/register">
                  <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="demo-signup-btn">
                    Get Started Free <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Step Quick Nav */}
        <div className="grid grid-cols-4 sm:grid-cols-4 md:grid-cols-8 gap-2 mt-8">
          {DEMO_STEPS.map((s, i) => {
            const StepIcon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => setCurrentStep(i)}
                className={`p-2.5 rounded-lg text-center transition-all ${
                  i === currentStep ? 'bg-primary text-white shadow-lg scale-105' : 'bg-muted/50 hover:bg-muted text-muted-foreground'
                }`}
                data-testid={`demo-nav-${s.id}`}
              >
                <StepIcon className="w-4 h-4 mx-auto mb-1" />
                <span className="text-[9px] font-medium block leading-tight">{s.title.split(' ').slice(0, 2).join(' ')}</span>
              </button>
            );
          })}
        </div>

        {/* CTA Banner */}
        <div className="mt-10 text-center bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl p-6 border border-primary/10">
          <h3 className="text-lg font-semibold mb-2">Ready to turn your emails into revenue?</h3>
          <p className="text-sm text-muted-foreground mb-4">Join professionals who are earning from every email they send.</p>
          <Link to="/register">
            <Button className="gap-2" data-testid="demo-final-cta">
              Create Free Account <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </Layout>
  );
}
